/**
 * Lane model and lane ADVICE.
 *
 * Painting arrows was not enough: on a multi-lane approach the driver read the
 * arrows, stayed in the leftmost lane because that is where you sit in Thai
 * traffic, and that lane turned out to be exit-only onto a different road.
 * Arrows tell you what each lane does; they do not tell you *which lane you
 * should be in right now*, and they do not shout when the lane you are
 * probably in is the wrong one.
 *
 * So on top of the arrows we compute:
 *   - the contiguous block of lanes that stays on route, as a position
 *     ("the 2 rightmost of 4"),
 *   - whether a lane change is genuinely required,
 *   - and a TRAP flag: the outermost lane on the side the driver naturally
 *     occupies is not valid. That is the case that nearly went wrong, and it
 *     gets an explicit spoken and visual warning.
 *
 * Mapbox orders `lanes` left-to-right in the direction of travel.
 */

import type { BannerInstruction, Lane, LaneIndication, RouteStep } from '../core/types';

/**
 * Lane data for the maneuver that BEGINS `step`, or null.
 *
 * The source is `intersections[0]` and nothing else. OSRM defines the
 * intersections array as "a list of Intersection objects that are passed along
 * the segment, the very first belonging to the StepManeuver" - so index 0 is
 * the junction the maneuver happens at, and its `lanes` are the turn lanes on
 * the approach to it. Every later entry is a junction we drive THROUGH after
 * the maneuver, and its lanes describe a decision the driver has already made.
 *
 * This function used to take the approach step and scan BACKWARDS for the last
 * intersection carrying lanes, which reads a different junction entirely.
 * Measured against six Thai routes (47 maneuvers) that produced the wrong lane
 * model for 24 of the 34 maneuvers that had one - 20 of them disagreeing even
 * on the number of lanes - and invented a lane bar for 13 maneuvers that have
 * no lane data at all, including `arrive`. It is the single cause behind every
 * field report the cross-checks below were bolted on to contain: the left turn
 * whose only usable lane was signed straight, the two right lanes that led to a
 * U-turn when the route went straight, the exit-only lane on the left.
 *
 * There is no fallback scan. If intersections[0] carries no lanes, this
 * maneuver has no lane data, and the correct output is nothing. Searching for
 * some other junction's lanes is what broke it.
 */
export function lanesForStep(step: RouteStep | null | undefined): Lane[] | null {
  const lanes = step?.intersections?.[0]?.lanes;
  return lanes?.length ? lanes : null;
}

/**
 * Lanes as Mapbox's own banner renders them. Preferred when present: the sub
 * banner is already filtered to the upcoming maneuver, whereas the raw
 * intersection model can describe a junction further along.
 */
export function lanesFromBanner(banner: BannerInstruction | null): Lane[] | null {
  const components = banner?.sub?.components;
  if (!components?.length) return null;
  const laneComponents = components.filter((c) => c.type === 'lane');
  if (!laneComponents.length) return null;

  return laneComponents.map((c) => ({
    valid: c.active === true,
    active: c.active === true,
    valid_indication: c.active_direction,
    indications: (c.directions ?? []) as LaneIndication[],
  }));
}

/**
 * Best available lane model for the maneuver that begins `maneuverStep`.
 *
 * `banner` comes from the step we are driving ALONG; the lane model comes from
 * the maneuver's own intersection. Both describe the same junction - checked
 * against six Thai routes, the banner sub and `intersections[0].lanes` agreed
 * on all 34 maneuvers that carried lane data, arrow for arrow.
 *
 * They are still merged rather than picked, because the intersection model is
 * the richer of the two: a banner lane component only says `active`, while an
 * intersection lane distinguishes `valid` (this lane can complete the
 * maneuver) from `active` (this is the lane to be in). That distinction is
 * what preferActive() below needs, and the banner cannot supply it.
 */
export function resolveLanes(
  banner: BannerInstruction | null,
  maneuverStep: RouteStep | null | undefined,
): Lane[] | null {
  const fromBanner = lanesFromBanner(banner);
  const fromRoad = lanesForStep(maneuverStep);
  if (!fromBanner) return fromRoad && preferActive(fromRoad);
  if (!fromRoad || fromRoad.length !== fromBanner.length) return fromBanner;

  const merged = fromBanner.map((b, i) => ({
    ...b,
    indications: b.indications.length ? b.indications : fromRoad[i].indications,
    valid_indication: b.valid_indication ?? fromRoad[i].valid_indication,
  }));
  // The banner has no "preferred" concept - its `active` merely means the lane
  // can complete the maneuver - so the road model supplies the narrowing.
  return preferActive(merged, fromRoad);
}

/**
 * Narrow "can" down to "should".
 *
 * Mapbox marks every lane that CAN complete the maneuver as `valid`, and
 * separately marks the lanes it actually recommends as `active` - the
 * preferred set accounts for what comes AFTER the turn. Advising the whole
 * valid set is how the app said "use the 2 left lanes" while the map's own
 * arrows took the turn from the far left lane only: both could turn, but only
 * the outer one lined up for the next maneuver. When a strictly smaller
 * preferred set exists, advise that and dim the rest.
 *
 * `activeSource` supplies the preferred flags positionally, so merged banner
 * lanes can be narrowed by the road model they were merged with.
 */
function preferActive(lanes: Lane[], activeSource: Lane[] = lanes): Lane[] {
  if (activeSource.length !== lanes.length) return lanes;
  const preferred = activeSource.map((l) => l.active === true);
  const nPreferred = preferred.filter(Boolean).length;
  if (nPreferred === 0 || nPreferred >= lanes.filter(laneValid).length) return lanes;

  const narrowed = lanes.map((l, i) => ({
    ...l,
    valid: laneValid(l) && preferred[i],
    active: laneValid(l) && preferred[i],
  }));
  // Contradictory sources - no preferred lane is even valid: the flags cannot
  // both be believed, so keep the broad set rather than advising nothing.
  return narrowed.some(laneValid) ? narrowed : lanes;
}

/** A lane counts as usable when Mapbox marks it valid (or active). */
export function laneValid(lane: Lane): boolean {
  return lane.valid === true || lane.active === true;
}

/** True when at least one lane is unusable - i.e. lane position actually matters. */
export function isLaneConstrained(lanes: Lane[] | null | undefined): boolean {
  if (!lanes?.length) return false;
  const valid = lanes.filter(laneValid).length;
  return valid > 0 && valid < lanes.length;
}

export interface LaneAdvice {
  lanes: Lane[];
  total: number;
  /** Indices (0 = leftmost) of lanes that stay on route. */
  validIndices: number[];
  /** Where the usable block sits across the carriageway. */
  side: 'left' | 'right' | 'centre' | 'all';
  /** How many lanes stay on route. */
  validCount: number;
  /** A lane change may be required - some lanes do not stay on route. */
  changeRequired: boolean;
  /**
   * The outermost lane on the side the driver naturally occupies is NOT valid.
   * In Thailand that is the left lane, and drifting into it is exactly the
   * mistake this flag exists to prevent.
   */
  trap: 'left' | 'right' | null;
}

/**
 * Which way a maneuver sends you across the carriageway.
 *
 * `measuredUturnSide` comes from the route geometry (nav/turnSide.ts) and wins
 * outright when present. U-turns in Thailand are on either side depending on
 * whether the junction is a median gap or a dedicated slip lane, so the
 * left-hand-traffic rule of thumb is a guess, not a fact.
 */
function maneuverSide(
  modifier: string | undefined,
  drivingSide: 'left' | 'right',
  measuredUturnSide?: 'left' | 'right' | null,
): 'left' | 'right' | 'straight' | null {
  const m = (modifier ?? '').toLowerCase();
  if (m === 'uturn') {
    if (measuredUturnSide) return measuredUturnSide;
    return drivingSide === 'left' ? 'right' : 'left';
  }
  if (m.includes('left')) return 'left';
  if (m.includes('right')) return 'right';
  if (m === 'straight' || m === '' || m === 'continue') return 'straight';
  return null;
}

/** Every arrow painted on a lane, including the one Mapbox marks as the one to follow. */
function laneSigns(lane: Lane): LaneIndication[] {
  const all = [...(lane.indications ?? [])];
  if (lane.valid_indication && !all.includes(lane.valid_indication)) all.push(lane.valid_indication);
  return all;
}

/** A lane that can ONLY take you round - useless for any other maneuver. */
function isUturnOnly(lane: Lane): boolean {
  const signs = laneSigns(lane);
  return signs.length > 0 && signs.every((s) => s === 'uturn');
}

/**
 * Turn a lane array into positional advice.
 *
 * `drivingSide` is where traffic keeps by default ('left' in Thailand).
 * `maneuverModifier`, when supplied, is used as a SAFETY CROSS-CHECK: lane data
 * that says "use the left lanes" for a right turn cannot be describing this
 * junction, and acting on it is how a driver ends up in an exit-only lane
 * heading somewhere else entirely. When the two disagree we return null and
 * show no lane guidance at all - saying nothing is safe, saying the wrong
 * thing is not.
 */
export function laneAdvice(
  lanes: Lane[] | null | undefined,
  drivingSide: 'left' | 'right' = 'left',
  maneuverModifier?: string,
  /** Measured side of a U-turn, from nav/turnSide.ts. */
  measuredUturnSide?: 'left' | 'right' | null,
): LaneAdvice | null {
  if (!lanes?.length) return null;

  const total = lanes.length;
  const validIndices = lanes.map((l, i) => (laneValid(l) ? i : -1)).filter((i) => i >= 0);
  const validCount = validIndices.length;

  // No lane data worth advising on: everything valid, or nothing is (bad data).
  if (validCount === 0) {
    return { lanes, total, validIndices, side: 'all', validCount, changeRequired: false, trap: null };
  }

  const first = validIndices[0];
  const last = validIndices[validIndices.length - 1];
  const changeRequired = validCount < total;

  let side: LaneAdvice['side'] = 'all';
  if (!changeRequired) side = 'all';
  else if (first === 0) side = 'left';
  else if (last === total - 1) side = 'right';
  else side = 'centre';

  // Safety cross-check. Mapbox occasionally hands back the lane model for a
  // neighbouring junction; positional advice derived from it points the driver
  // the wrong way across the carriageway. Refuse to guess.
  const wanted = maneuverSide(maneuverModifier, drivingSide, measuredUturnSide);
  const isUturn = (maneuverModifier ?? '').toLowerCase() === 'uturn';
  if (
    changeRequired &&
    ((wanted === 'left' && side === 'right') || (wanted === 'right' && side === 'left'))
  ) {
    return null;
  }

  /*
   * Second check, using what the lanes themselves claim rather than where they
   * sit. This one rejects on CONTRADICTION only - signs pointing the opposite
   * way - and not on mere absence of confirmation.
   *
   * It used to demand that some usable lane be signed left-ish for a left
   * maneuver. That was written to catch a left turn whose only valid lane was
   * signed STRAIGHT, but the real cause of that was reading a different
   * junction's lanes entirely (see lanesForStep), and with the source fixed the
   * rule does far more harm than good: `straight` is the NORMAL sign for the
   * through carriageway at a fork or a ramp. Measured over six Thai routes it
   * threw away 14 of 34 correct lane models - among them every motorway fork,
   * where the branch you stay on is signed straight and only the branch you
   * leave is signed slight left. Those are precisely the junctions where the
   * driver most needs to be told which side to hold.
   *
   * So `straight` satisfies any maneuver. What cannot be believed is a usable
   * lane signed only toward the other side of the road, or only back the way we
   * came - if EVERY usable lane says that, the model is not describing this
   * maneuver and we show nothing.
   */
  const turning = wanted === 'left' || wanted === 'right';
  if (changeRequired && turning && !isUturn) {
    const opposite = wanted === 'left' ? 'right' : 'left';
    const usable = validIndices.map((i) => laneSigns(lanes[i])).filter((s) => s.length > 0);
    // Only judge when Mapbox actually told us; otherwise fall back to position.
    const contradicts = (signs: LaneIndication[]): boolean =>
      signs.every((s) => s === 'uturn' || (s.includes(opposite) && !s.includes(wanted)));
    if (usable.length && usable.every(contradicts)) return null;
  }

  /*
   * Straight ahead had NO cross-check at all, and that is the failure the
   * driver hit: "two lanes were on the right for u turn, and the gps told us
   * to stay in the 2 right lanes, that would take us to the u turn, but we
   * were supposed to go straight."
   *
   * When the route continues straight, at least one usable lane has to be
   * signed straight. If the only lanes on offer turn or double back, the lane
   * model is describing a different maneuver than the one we are driving, and
   * following it takes the vehicle off the route entirely.
   */
  if (changeRequired && wanted === 'straight') {
    const anyStraight = validIndices.some((i) =>
      laneSigns(lanes[i]).some((sign) => sign.includes('straight')),
    );
    const anySigned = validIndices.some((i) => laneSigns(lanes[i]).length > 0);
    if (anySigned && !anyStraight) return null;
  }

  // Belt and braces for every non-U-turn maneuver: if every lane we would send
  // the driver into is U-turn-only, the advice is actively dangerous.
  if (changeRequired && !isUturn && validIndices.every((i) => isUturnOnly(lanes[i]))) {
    return null;
  }

  // The trap: the lane the driver is most likely already in cannot be used.
  const defaultLane = drivingSide === 'left' ? 0 : total - 1;
  const trap =
    changeRequired && !laneValid(lanes[defaultLane])
      ? drivingSide === 'left'
        ? 'left'
        : 'right'
      : null;

  return { lanes, total, validIndices, side, validCount, changeRequired, trap };
}

/**
 * How far out lane guidance should appear, in metres.
 * Scales with speed: at 100 km/h you need ~25 s to cross three lanes safely,
 * at 30 km/h in town 250 m is ample and anything more is clutter.
 */
export function laneVisibilityRange(speedMs: number): number {
  return Math.min(1400, Math.max(250, speedMs * 25));
}
